import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  House,
  Upload,
  Lightning,
  CurrencyCircleDollar,
  FileText,
  Gear,
  SignOut,
  Bell,
  List,
  CaretDown,
  Users,
  ShieldCheck,
  Books,
  Megaphone,
  Bank,
} from '@phosphor-icons/react';
import EspertoArera from './EspertoArera';
import NotificationBell from './NotificationBell';

// Sprint 46 Task 3 — Menu riorganizzato nel flusso commerciale a 5 step.
// Ogni macro-sezione è numerata per riflettere la sequenza operativa:
//   1. Acquisizione → 2. Vendita → 3. Gestione Cliente → 4. Back Office → 5. Adempimenti
const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: House },

  // --- 1. ACQUISIZIONE ---------------------------------------------------
  {
    id: 'step-acquisizione',
    label: '1. Acquisizione',
    icon: Upload,
    roles: ['admin', 'commerciale'],
    children: [
      { path: '/leads', label: 'Lead', roles: ['admin', 'commerciale'] },
      { path: '/fatture-ocr', label: 'Analisi Fatture (OCR)', roles: ['admin', 'commerciale'] },
      { path: '/folder', label: 'Archivio', roles: ['admin'] },
    ],
  },

  // --- 2. VENDITA --------------------------------------------------------
  {
    id: 'step-vendita',
    label: '2. Vendita',
    icon: CurrencyCircleDollar,
    roles: ['admin', 'commerciale'],
    children: [
      { path: '/pre-contracts', label: 'Precontratti', roles: ['admin', 'commerciale'] },
      { path: '/offers', label: 'CTE & Offerte', roles: ['admin'] },
      { path: '/funnel', label: 'Funnel Vendite', roles: ['admin'] },
      { path: '/approvals', label: 'Approvazioni sconto', roles: ['admin', 'commerciale'], badge: 'approvals_pending' },
    ],
  },

  // --- 3. BACK OFFICE (prima del cliente attivo) ------------------------
  {
    id: 'step-backoffice',
    label: '3. Back Office',
    icon: ShieldCheck,
    roles: ['admin', 'back_office'],
    children: [
      { path: '/contracts', label: 'Contratti da Validare', roles: ['admin', 'back_office'] },
      { path: '/pods/import', label: 'Import POD', roles: ['admin', 'back_office'] },
      { path: '/rcu/import', label: 'Import RCU', roles: ['admin', 'back_office'] },
      { path: '/pods/active', label: 'POD Attivi', roles: ['admin', 'back_office'] },
      { path: '/provinces/active', label: 'Province Attive', roles: ['admin', 'back_office'] },
    ],
  },

  // --- 4. GESTIONE CLIENTE (cliente già attivato) -----------------------
  {
    id: 'step-gestione',
    label: '4. Gestione Cliente',
    icon: Users,
    roles: ['admin'],
    children: [
      { path: '/customers', label: 'Clienti Attivi', roles: ['admin'] },
    ],
  },

  // --- 5. ADEMPIMENTI ----------------------------------------------------
  {
    id: 'step-adempimenti',
    label: '5. Adempimenti',
    icon: FileText,
    roles: ['admin', 'back_office'],
    badge: 'compliance_unread',
    children: [
      { path: '/declarations', label: 'Dichiarazioni', roles: ['admin', 'back_office'] },
      { path: '/payments', label: 'Versamenti F24', roles: ['admin', 'back_office'] },
      { path: '/payments/generate', label: 'Genera F24', roles: ['admin', 'back_office'] },
      { path: '/payments/history', label: 'Storico F24', roles: ['admin', 'back_office'] },
      { path: '/communications/pending', label: 'Comunicazioni Dogane', roles: ['admin', 'back_office'] },
      { path: '/communications/sent', label: 'Comunicazioni Inviate', roles: ['admin', 'back_office'] },
      { id: 'arera', label: 'ARERA', icon: Books, children: [
        { path: '/knowledge', label: 'Knowledge Base' },
        { path: '/compliance', label: 'Raccolte dati e richieste' },
        { path: '/compliance/reports', label: 'Relazioni delibere' },
        { path: '/compliance/settings', label: 'Impostazioni notifiche' },
      ]},
      { path: '/compliance/dogane', label: 'Dogane', roles: ['admin', 'back_office'] },
      { path: '/compliance/acquirente-unico', label: 'Acquirente Unico', roles: ['admin', 'back_office'] },
    ],
  },

  // --- CREDITO (verifica affidabilità P.IVA — Sprint 6) -----------------
  {
    id: 'credito-group',
    label: 'Credito',
    icon: Bank,
    roles: ['admin', 'commerciale'],
    children: [
      { path: '/credito/portafoglio', label: 'Portafoglio Clienti' },
      { path: '/credito/lead', label: 'Lead' },
      { path: '/credito/ricerca', label: 'Ricerca P.IVA' },
    ],
  },

  // --- MARKETING (sezione trasversale, non numerata) --------------------
  {
    id: 'marketing',
    label: 'Marketing',
    icon: Megaphone,
    roles: ['admin'],
    children: [
      { path: '/marketing/groups', label: 'Gruppi' },
      { path: '/marketing/campaigns', label: 'Campagne' },
      { path: '/marketing/communications', label: 'Comunicazioni' },
    ],
  },

  // --- Impostazioni (sempre in fondo) -----------------------------------
  {
    id: 'impostazioni-group',
    label: 'Impostazioni',
    icon: Gear,
    roles: ['admin'],
    children: [
      { path: '/settings', label: 'Azienda & Comunicazioni' },
      { path: '/settings/brand', label: 'Brand & Documenti' },
      { path: '/settings/users', label: 'Utenti & Permessi' },
    ],
  },
];

// Returns a filtered copy of NAV_ITEMS honouring `roles` hints. An item without
// `roles` is visible to everyone. Admin sees everything regardless.
const filterNavByRole = (items, role) => {
  if (!items || role === 'admin') return items;
  return items
    .filter(item => !item.roles || item.roles.includes(role))
    .map(item => item.children ? { ...item, children: filterNavByRole(item.children, role) } : item)
    .filter(item => !item.children || item.children.length > 0);
};

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Sprint 8: role-aware sidebar — hide entire sections not accessible to this role.
  const visibleNav = useMemo(
    () => filterNavByRole(NAV_ITEMS, user?.role || 'user'),
    [user?.role],
  );

  const getInitialOpen = () => {
    const open = {};
    visibleNav.forEach((item) => {
      if (item.children) {
        const allPaths = item.children.flatMap((c) =>
          c.children ? c.children.map((g) => g.path) : [c.path]
        );
        if (allPaths.includes(location.pathname)) open[item.id] = true;
      }
    });
    return open;
  };

  const [openGroups, setOpenGroups] = useState(getInitialOpen);
  const [complianceUnread, setComplianceUnread] = useState(0);
  const [reportsUnread, setReportsUnread] = useState(0);
  const [approvalsPending, setApprovalsPending] = useState(0);
  const [recentReports, setRecentReports] = useState([]);
  const [pushOpen, setPushOpen] = useState(false);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    const fetchUnread = async () => {
      try {
        const [aRes, rRes, apRes] = await Promise.all([
          fetch(`${API}/api/compliance/alerts?unread_only=true&limit=1`, { credentials: 'include' }),
          fetch(`${API}/api/compliance/reports?unread_only=true&limit=8`, { credentials: 'include' }),
          fetch(`${API}/api/extra-sconto/pending-count`, { credentials: 'include' }),
        ]);
        if (aRes.ok) {
          const data = await aRes.json();
          setComplianceUnread(data.stats?.unread || 0);
        }
        if (rRes.ok) {
          const rd = await rRes.json();
          setReportsUnread(rd.unread || 0);
          setRecentReports(rd.reports || []);
        }
        if (apRes.ok) {
          const ap = await apRes.json();
          setApprovalsPending(ap.count || 0);
        }
      } catch (e) { /* ignore */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 60000);
    return () => clearInterval(t);
  }, [location.pathname]);

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      visibleNav.forEach((item) => {
        if (item.children) {
          const allPaths = item.children.flatMap((c) =>
            c.children ? c.children.map((g) => g.path) : [c.path]
          );
          if (allPaths.includes(location.pathname)) next[item.id] = true;
        }
      });
      return next;
    });
  }, [location.pathname]);

  const toggleGroup = (id) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="fixed top-0 left-0 right-0 z-50 h-16 bg-white border-b border-slate-200">
        <div className="h-full px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 hover:bg-slate-100 rounded-sm transition-colors"
              data-testid="mobile-menu-toggle"
            >
              <List size={24} className="text-slate-700" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-[#0A2540] rounded-sm flex items-center justify-center">
                <Lightning size={20} className="text-white" weight="bold" />
              </div>
              <div>
                <h1 className="text-base font-bold tracking-tight text-slate-900 leading-none">Orange Energy</h1>
                <p className="text-[10px] text-slate-500 leading-none mt-0.5">Gestione Clienti & Accise</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <EspertoArera />
            <NotificationBell />
            <div className="relative">
              <button onClick={() => setPushOpen(!pushOpen)}
                      className="relative p-2 hover:bg-slate-100 rounded-sm transition-colors"
                      title={`${complianceUnread} alert · ${reportsUnread} relazioni`}
                      data-testid="notifications-button">
                <Bell size={20} className="text-slate-600" />
                {(complianceUnread + reportsUnread) > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                    {complianceUnread + reportsUnread}
                  </span>
                )}
              </button>
              {pushOpen && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setPushOpen(false)} />
                  <div className="absolute right-0 mt-2 w-96 bg-white border rounded-lg shadow-xl z-40 max-h-[70vh] overflow-y-auto">
                    <div className="p-3 border-b flex items-center justify-between">
                      <span className="font-semibold">Notifiche Compliance</span>
                      <button onClick={() => { navigate('/compliance/reports'); setPushOpen(false); }}
                              className="text-xs text-orange-600 hover:underline">Vedi tutte →</button>
                    </div>
                    {complianceUnread > 0 && (
                      <button onClick={() => { navigate('/compliance'); setPushOpen(false); }}
                              className="w-full text-left p-3 hover:bg-orange-50 border-b flex items-center gap-3">
                        <Bell size={18} className="text-orange-500" />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{complianceUnread} alert ARERA da leggere</div>
                          <div className="text-xs text-slate-500">Nuove delibere/testi integrati rilevati</div>
                        </div>
                      </button>
                    )}
                    {recentReports.length === 0 && reportsUnread === 0 && complianceUnread === 0 && (
                      <div className="p-8 text-center text-slate-400 text-sm">Nessuna notifica</div>
                    )}
                    {recentReports.map(r => (
                      <button key={r.id}
                              onClick={() => { navigate('/compliance/reports'); setPushOpen(false); }}
                              className="w-full text-left p-3 hover:bg-orange-50 border-b">
                        <div className="flex items-start gap-2">
                          <span className="mt-1 inline-block w-2 h-2 bg-orange-500 rounded-full shrink-0"></span>
                          <div className="flex-1 min-w-0">
                            <div className="font-mono text-[10px] text-orange-600">{r.delibera_id || 'Delibera'}</div>
                            <div className="text-sm font-medium text-slate-900 truncate">{r.title}</div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              📑 Relazione pronta · {new Date(r.created_at).toLocaleDateString('it-IT')}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-[#0066FF] flex items-center justify-center">
                <span className="text-white font-semibold text-sm">{user?.name?.charAt(0).toUpperCase()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex pt-16">
        <aside
          className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 transition-transform lg:translate-x-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } pt-16 lg:pt-0 overflow-y-auto`}
        >
          <nav className="h-full flex flex-col p-4">
            <div className="flex-1 space-y-1">
              {visibleNav.map((item) => {
                if (item.children) {
                  const isOpen = openGroups[item.id] || false;
                  const allChildPaths = item.children.flatMap((c) =>
                    c.children ? c.children.map((g) => g.path) : [c.path]
                  );
                  const hasActiveChild = allChildPaths.includes(location.pathname);
                  const Icon = item.icon;

                  return (
                    <div key={item.id} data-testid={`nav-group-${item.id}`}>
                      <button
                        onClick={() => toggleGroup(item.id)}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-sm font-medium transition-colors text-sm ${
                          hasActiveChild ? 'text-slate-900 bg-slate-50' : 'text-slate-700 hover:bg-slate-50'
                        }`}
                        data-testid={`nav-toggle-${item.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon size={20} weight={hasActiveChild ? 'fill' : 'regular'} />
                          <span>{item.label}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {item.badge === 'compliance_unread' && complianceUnread > 0 && (
                            <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5"
                                  data-testid={`group-badge-${item.id}`}>
                              {complianceUnread > 99 ? '99+' : complianceUnread}
                            </span>
                          )}
                          {item.badge === 'approvals_pending' && approvalsPending > 0 && (
                            <span className="bg-amber-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5"
                                  data-testid={`group-badge-${item.id}`}>
                              {approvalsPending > 99 ? '99+' : approvalsPending}
                            </span>
                          )}
                          <CaretDown
                            size={16}
                            className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                          />
                        </div>
                      </button>
                      <div
                        className={`overflow-hidden transition-all duration-200 ${
                          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="ml-4 mt-1 space-y-0.5 pb-1">
                          {item.children.map((child) => {
                            // Support 1 level of nested groups (e.g. Compliance > ARERA > Knowledge)
                            if (child.children) {
                              return (
                                <div key={child.id} className="pt-1">
                                  <div className="flex items-center gap-2 px-4 py-1.5 text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                                    {child.icon && <child.icon size={12} />}
                                    {child.label}
                                  </div>
                                  {child.children.map((grand) => (
                                    <Link
                                      key={grand.path}
                                      to={grand.path}
                                      onClick={() => setSidebarOpen(false)}
                                      className={`block px-6 py-1.5 text-sm rounded-sm transition-colors ${
                                        location.pathname === grand.path
                                          ? 'bg-slate-100 text-slate-900 font-medium'
                                          : 'text-slate-600 hover:bg-slate-50'
                                      }`}
                                      data-testid={`nav-link-${grand.path.replace(/\//g, '-').slice(1)}`}
                                    >
                                      {grand.label}
                                    </Link>
                                  ))}
                                </div>
                              );
                            }
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                onClick={() => setSidebarOpen(false)}
                                className={`block px-4 py-2 text-sm rounded-sm transition-colors ${
                                  location.pathname === child.path
                                    ? 'bg-slate-100 text-slate-900 font-medium'
                                    : 'text-slate-600 hover:bg-slate-50'
                                }`}
                                data-testid={`nav-link-${child.path.replace(/\//g, '-').slice(1)}`}
                              >
                                {child.label}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }

                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-sm font-medium transition-colors text-sm ${
                      isActive
                        ? 'bg-[#0A2540] text-white'
                        : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                    data-testid={`nav-${item.path.replace('/', '')}`}
                  >
                    <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                    <span>{item.label}</span>
                    {item.badge === 'compliance_unread' && complianceUnread > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5"
                            data-testid="compliance-unread-badge">
                        {complianceUnread > 99 ? '99+' : complianceUnread}
                      </span>
                    )}
                    {item.badge === 'approvals_pending' && approvalsPending > 0 && (
                      <span className="ml-auto bg-amber-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5"
                            data-testid="approvals-pending-badge">
                        {approvalsPending > 99 ? '99+' : approvalsPending}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>

            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 text-slate-700 hover:bg-red-50 hover:text-red-600 rounded-sm font-medium transition-colors mt-4 text-sm"
              data-testid="logout-button"
            >
              <SignOut size={20} />
              <span>Esci</span>
            </button>
          </nav>
        </aside>

        {sidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        <main className="flex-1 p-6 md:p-8 lg:p-12">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
